import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit2, Search, User, Phone, Mail,
  MapPin, FileText, ChevronLeft, Loader2, Archive,
  Truck, ArrowLeftRight, Globe, Eye, TrendingUp,
  CreditCard, AlertCircle, Clock, Check, Calendar,
  BarChart3, Receipt, Star, ArrowUpDown, Filter,
  ArrowUp, ArrowDown, Zap, ExternalLink, Download
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// PARTENAIRES MODULE — Module complet annuaire clients / fournisseurs
// ============================================================================

const TYPES_PARTENAIRE = [
  { value: 'client', label: 'Client', icon: User, color: '#10b981', bg: '#d4f4dd' },
  { value: 'fournisseur', label: 'Fournisseur', icon: Truck, color: '#f59e0b', bg: '#fef3c7' },
  { value: 'les_deux', label: 'Client & Fourn.', icon: ArrowLeftRight, color: '#6366f1', bg: '#e0e7ff' },
];

const STATUT_FACTURE = {
  brouillon: { label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
  envoyee: { label: 'Envoyée', color: '#3b82f6', bg: '#eff6ff' },
  payee: { label: 'Payée', color: '#10b981', bg: '#d4f4dd' },
  partielle: { label: 'Partielle', color: '#f59e0b', bg: '#fef3c7' },
  en_retard: { label: 'En retard', color: '#ef4444', bg: '#fee2e2' },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6' },
};

const MOIS_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const TRI_OPTIONS = [
  { value: '', label: 'Nom A→Z', icon: ArrowUpDown },
  { value: 'ca', label: 'CA le + élevé', icon: ArrowDown },
  { value: 'impayes', label: 'Impayés', icon: AlertCircle },
  { value: 'recent', label: 'Plus récent', icon: Clock },
];

// Score de fiabilité client (0–100)
const getScoreClient = (p) => {
  let score = 50;
  const ca = parseFloat(p.ca_total || 0);
  const impaye = parseFloat(p.total_impaye || 0);
  const nbFact = parseInt(p.nb_factures || 0);

  if (nbFact === 0) return null; // Pas encore de facture
  if (ca > 0 && impaye === 0) score += 30; // Tout payé
  else if (ca > 0 && impaye > 0) {
    const ratio = impaye / ca;
    if (ratio < 0.1) score += 20;
    else if (ratio < 0.3) score += 5;
    else if (ratio > 0.5) score -= 20;
    else score -= 10;
  }
  if (nbFact >= 5) score += 10;
  if (nbFact >= 10) score += 10;
  return Math.max(0, Math.min(100, score));
};

const getScoreColor = (score) => {
  if (score === null) return '#9ca3af';
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const getScoreLabel = (score) => {
  if (score === null) return 'Nouveau';
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Correct';
  return 'À risque';
};

const PartenairesModule = () => {
  // Liste
  const [partenaires, setPartenaires] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [triActif, setTriActif] = useState('');

  // Vues
  const [currentView, setCurrentView] = useState('list');
  const [selectedPartenaire, setSelectedPartenaire] = useState(null);

  // Stats détail
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('apercu');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    type_partenaire: 'client', nom: '', nom_commercial: '',
    email: '', telephone: '', telephone_2: '', site_web: '',
    adresse: '', ville: '', pays: 'Congo',
    nif: '', rccm: '', id_national: '',
    conditions_paiement: '', delai_paiement_jours: 30, notes: '',
  });

  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);
  const [showTriMenu, setShowTriMenu] = useState(false);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => { loadPartenaires(); }, [filterType, triActif]);
  useEffect(() => { const t = setTimeout(() => loadPartenaires(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadPartenaires = async () => {
    showLoader(); setIsLoading(true);
    try {
      const r = await api.partenaires.getAll({ type: filterType, search: searchTerm, tri: triActif });
      if (r.success) { setPartenaires(r.data); setTotaux(r.totaux || {}); }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadDetail = async (partenaire) => {
    setSelectedPartenaire(partenaire);
    setCurrentView('detail');
    setDetailTab('apercu');
    setLoadingDetail(true);
    try {
      const r = await api.partenaires.getStats(partenaire.id);
      if (r.success) setDetailData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  // ============================================================================
  // CRUD
  // ============================================================================
  const resetForm = (type = 'client') => ({
    type_partenaire: type, nom: '', nom_commercial: '', email: '', telephone: '', telephone_2: '', site_web: '', adresse: '', ville: '', pays: 'Congo', nif: '', rccm: '', id_national: '', conditions_paiement: '', delai_paiement_jours: 30, notes: ''
  });

  const openCreateModal = (type = 'client') => {
    setModalMode('create'); setEditingPartenaire(null); setFormData(resetForm(type)); setShowModal(true);
  };

  const openEditModal = (p) => {
    setModalMode('edit'); setEditingPartenaire(p);
    setFormData({
      type_partenaire: p.type_partenaire, nom: p.nom, nom_commercial: p.nom_commercial || '',
      email: p.email || '', telephone: p.telephone || '', telephone_2: p.telephone_2 || '',
      site_web: p.site_web || '', adresse: p.adresse || '', ville: p.ville || '',
      pays: p.pays || 'Congo', nif: p.nif || '', rccm: p.rccm || '',
      id_national: p.id_national || '', conditions_paiement: p.conditions_paiement || '',
      delai_paiement_jours: p.delai_paiement_jours || 30, notes: p.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) { showToast('Le nom est obligatoire', 'error'); return; }
    setIsSaving(true);
    try {
      const r = modalMode === 'create' ? await api.partenaires.creer(formData) : await api.partenaires.modifier(editingPartenaire.id, formData);
      if (r.success) {
        showToast(r.message); setShowModal(false); await loadPartenaires();
        if (r.data && currentView === 'detail') loadDetail(r.data);
      } else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleArchive = async (p) => {
    try {
      const r = await api.partenaires.archiver(p.id);
      if (r.success) { showToast('Archivé'); if (currentView === 'detail') { setCurrentView('list'); setSelectedPartenaire(null); } await loadPartenaires(); }
    } catch (e) { showToast('Erreur', 'error'); }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  const getTypeConfig = (type) => TYPES_PARTENAIRE.find(t => t.value === type) || TYPES_PARTENAIRE[0];
 
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const getBarWidth = (v, max) => max > 0 ? Math.min((v / max) * 100, 100) : 0;

  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Users size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Total partenaires</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><User size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Clients</span></div>
          <div className="text-2xl font-bold text-green-600">{totaux.nb_clients || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Truck size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">Fournisseurs</span></div>
          <div className="text-2xl font-bold text-amber-600">{totaux.nb_fournisseurs || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><AlertCircle size={18} className="text-red-600" /><span className="text-xs font-semibold text-gray-500">Impayés total</span></div>
          <div className="text-lg font-bold text-red-600">{formatMontant(totaux.impayes_global)}</div>
        </div>
        <div onClick={() => openCreateModal()} className="bg-white rounded-2xl p-5 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696]">Nouveau</div><div className="text-xs text-gray-400">Ajouter un partenaire</div></div>
          </div>
        </div>
      </div>

      {/* Recherche + filtres + tri */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher par nom, email, téléphone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all"
              {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{ value: '', label: 'Tous' }, ...TYPES_PARTENAIRE].map(t => (
              <button key={t.value} onClick={() => setFilterType(t.value)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-2 ${filterType === t.value ? 'shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                style={filterType === t.value ? { borderColor: t.color || '#448696', backgroundColor: t.bg || '#f0f9fb', color: t.color || '#448696' } : {}}>
                {t.label}
              </button>
            ))}
            {/* Tri */}
            <div className="relative">
              <button onClick={() => setShowTriMenu(!showTriMenu)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-2 flex items-center gap-1 ${triActif ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <ArrowUpDown size={13} /> Trier
              </button>
              {showTriMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl z-50 py-1 min-w-[160px]">
                  {TRI_OPTIONS.map(t => (
                    <button key={t.value} onClick={() => { setTriActif(t.value); setShowTriMenu(false); }}
                      className={`w-full px-4 py-2.5 text-left text-xs font-semibold transition-all flex items-center gap-2 ${triActif === t.value ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <t.icon size={13} /> {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      {isLoading ? null : partenaires.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun partenaire</h3>
          {!searchTerm && <button onClick={() => openCreateModal()} className="mt-4 px-6 py-3 text-white rounded-xl font-semibold text-sm shadow-lg" style={{ backgroundColor: '#448696' }}><Plus className="w-4 h-4 inline mr-2" />Ajouter</button>}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="divide-y divide-gray-100">
            {partenaires.map((p) => {
              const tc = getTypeConfig(p.type_partenaire);
              const TIcon = tc.icon;
              const ca = parseFloat(p.ca_total || 0);
              const impaye = parseFloat(p.total_impaye || 0);
              const score = getScoreClient(p);
              const scoreColor = getScoreColor(score);

              return (
                <div key={p.id} onClick={() => loadDetail(p)} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    {/* Avatar + score */}
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: tc.bg }}>
                        <TIcon size={22} style={{ color: tc.color }} />
                      </div>
                      {score !== null && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold border-2 border-white"
                          style={{ backgroundColor: scoreColor }}>
                          {score}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-gray-900 truncate">{p.nom}</span>
                        {p.nom_commercial && <span className="text-xs text-gray-400 truncate">({p.nom_commercial})</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                        {p.telephone && <span className="flex items-center gap-1"><Phone size={11} />{p.telephone}</span>}
                        {p.email && <span className="flex items-center gap-1"><Mail size={11} />{p.email}</span>}
                        {p.ville && <span className="flex items-center gap-1"><MapPin size={11} />{p.ville}</span>}
                      </div>
                    </div>

                    {/* Solde financier */}
                    <div className="text-right shrink-0 min-w-[120px]">
                      {ca > 0 ? (
                        <>
                          <div className="text-sm font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(ca)}</div>
                          {impaye > 0 && (
                            <div className="text-xs font-semibold text-red-500 flex items-center gap-1 justify-end">
                              <AlertCircle size={10} /> {formatMontant(impaye)}
                            </div>
                          )}
                          {impaye === 0 && ca > 0 && (
                            <div className="text-xs font-semibold text-green-500 flex items-center gap-1 justify-end">
                              <Check size={10} /> Soldé
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-gray-300 italic">Pas de facture</div>
                      )}
                    </div>

                    {/* Badge type */}
                    <span className="text-xs px-2.5 py-1 rounded-lg font-semibold shrink-0" style={{ backgroundColor: tc.bg, color: tc.color }}>{tc.label}</span>

                    {/* Edit */}
                    <button onClick={(e) => { e.stopPropagation(); openEditModal(p); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-gray-100">
                      <Edit2 size={14} className="text-gray-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (!selectedPartenaire) return null;
    const p = selectedPartenaire;
    const tc = getTypeConfig(p.type_partenaire);
    const TIcon = tc.icon;
    const stats = detailData?.stats;
    const factures = detailData?.factures || [];
    const paiements = detailData?.paiements || [];
    const evolution = detailData?.evolution || [];
    const maxEvo = Math.max(...evolution.map(e => parseFloat(e.ca)), 1);

    const score = stats ? getScoreClient({ ca_total: stats.ca_total, total_impaye: stats.total_impaye, nb_factures: stats.nb_factures }) : null;
    const scoreColor = getScoreColor(score);
    const scoreLabel = getScoreLabel(score);

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedPartenaire(null); setDetailData(null); }}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour à la liste
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${tc.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: tc.bg }}>
                <TIcon size={32} style={{ color: tc.color }} />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{p.nom}</h2>
                {p.nom_commercial && <p className="text-sm text-gray-500 font-medium">{p.nom_commercial}</p>}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs px-3 py-1 rounded-lg font-semibold" style={{ backgroundColor: tc.bg, color: tc.color }}>{tc.label}</span>
                  {score !== null && (
                    <span className="text-xs px-3 py-1 rounded-lg font-bold text-white flex items-center gap-1" style={{ backgroundColor: scoreColor }}>
                      <Star size={10} /> {score}/100 — {scoreLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEditModal(p)} className="px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
                <Edit2 size={14} className="inline mr-2" />Modifier
              </button>
              <button onClick={() => setConfirmDialogConfig({ partenaire: p })} className="px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-red-200 text-red-600 hover:bg-red-50 transition-all">
                <Archive size={14} className="inline mr-2" />Archiver
              </button>
              <button onClick={async () => {
                try {
                  showToast('Génération de la fiche PDF...', 'loading');
                  await api.partenaires.exportPDF(p.id);
                  showToast('Fiche PDF téléchargée !', 'success');
                } catch (e) { showToast('Erreur génération PDF', 'error'); }
              }} className="px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
                <Download size={14} className="inline mr-2" />Fiche PDF
              </button>
            </div>
          </div>

          {/* Barre solde visuelle */}
          {stats && stats.ca_total > 0 && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">Paiements reçus vs CA total</span>
                <span className="text-xs font-bold" style={{ color: stats.total_impaye > 0 ? '#ef4444' : '#10b981' }}>
                  {stats.total_impaye > 0 ? `${formatMontant(stats.total_impaye)} impayés` : 'Tout payé ✓'}
                </span>
              </div>
              <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="h-full rounded-l-full transition-all duration-500" style={{ width: `${getBarWidth(stats.total_paye, stats.ca_total)}%`, backgroundColor: '#10b981' }} />
                {stats.total_impaye > 0 && (
                  <div className="h-full rounded-r-full transition-all duration-500" style={{ width: `${getBarWidth(stats.total_impaye, stats.ca_total)}%`, backgroundColor: '#fee2e2' }} />
                )}
              </div>
              <div className="flex justify-between mt-1 text-[10px] font-semibold">
                <span className="text-green-600">Payé: {formatMontant(stats.total_paye)}</span>
                <span className="text-gray-400">CA: {formatMontant(stats.ca_total)}</span>
              </div>
            </div>
          )}
        </div>

        {loadingDetail ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
        ) : stats ? (
          <>
            {/* KPIs financiers */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #6366f1', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-xs font-semibold text-gray-500 mb-1">CA Total</div>
                <div className="text-lg font-bold text-indigo-600">{formatMontant(stats.ca_total)}</div>
              </div>
              <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-xs font-semibold text-gray-500 mb-1">Total payé</div>
                <div className="text-lg font-bold text-green-600">{formatMontant(stats.total_paye)}</div>
              </div>
              <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-xs font-semibold text-gray-500 mb-1">Impayés</div>
                <div className="text-lg font-bold text-red-600">{formatMontant(stats.total_impaye)}</div>
              </div>
              <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-xs font-semibold text-gray-500 mb-1">Factures / Devis</div>
                <div className="text-lg font-bold text-amber-600">{stats.nb_factures} / {stats.nb_devis}</div>
              </div>
              <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="text-xs font-semibold text-gray-500 mb-1">Délai moyen</div>
                <div className="text-lg font-bold" style={{ color: '#448696' }}>{stats.delai_moyen_paiement ? `${stats.delai_moyen_paiement}j` : '-'}</div>
              </div>
            </div>

            {/* Onglets */}
            <div className="flex gap-2 mb-5">
              {[
                { id: 'apercu', label: 'Aperçu', icon: Eye },
                { id: 'factures', label: `Factures (${factures.length})`, icon: FileText },
                { id: 'paiements', label: `Paiements (${paiements.length})`, icon: CreditCard },
              ].map(tab => (
                <button key={tab.id} onClick={() => setDetailTab(tab.id)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all border-2 flex items-center gap-2 ${detailTab === tab.id ? 'border-[#448696] shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  style={detailTab === tab.id ? { borderColor: '#448696', backgroundColor: '#f0f9fb', color: '#448696' } : {}}>
                  <tab.icon size={14} />{tab.label}
                </button>
              ))}
            </div>

            {/* TAB: Aperçu */}
            {detailTab === 'apercu' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contact */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><Phone size={16} style={{ color: '#448696' }} />Contact</h3>
                  <div className="space-y-3">
                    {p.telephone && <div className="flex items-center gap-3"><Phone size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{p.telephone}</span></div>}
                    {p.telephone_2 && <div className="flex items-center gap-3"><Phone size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{p.telephone_2}</span></div>}
                    {p.email && <div className="flex items-center gap-3"><Mail size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{p.email}</span></div>}
                    {p.site_web && <div className="flex items-center gap-3"><Globe size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{p.site_web}</span></div>}
                    {p.adresse && <div className="flex items-center gap-3"><MapPin size={14} className="text-gray-400" /><span className="text-sm font-medium text-gray-700">{p.adresse}{p.ville ? `, ${p.ville}` : ''}{p.pays ? ` — ${p.pays}` : ''}</span></div>}
                    {!p.telephone && !p.email && !p.adresse && <p className="text-sm text-gray-400 italic">Aucune info de contact</p>}
                  </div>
                </div>

                {/* Infos légales */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FileText size={16} style={{ color: '#448696' }} />Infos légales</h3>
                  <div className="space-y-3">
                    {p.nif && <div className="flex justify-between"><span className="text-xs font-semibold text-gray-500">NIF</span><span className="text-sm font-medium text-gray-700">{p.nif}</span></div>}
                    {p.rccm && <div className="flex justify-between"><span className="text-xs font-semibold text-gray-500">RCCM</span><span className="text-sm font-medium text-gray-700">{p.rccm}</span></div>}
                    {p.id_national && <div className="flex justify-between"><span className="text-xs font-semibold text-gray-500">ID National</span><span className="text-sm font-medium text-gray-700">{p.id_national}</span></div>}
                    <div className="flex justify-between"><span className="text-xs font-semibold text-gray-500">Délai paiement</span><span className="text-sm font-medium text-gray-700">{p.delai_paiement_jours || 30} jours</span></div>
                    {p.conditions_paiement && <div className="flex justify-between"><span className="text-xs font-semibold text-gray-500">Conditions</span><span className="text-sm font-medium text-gray-700">{p.conditions_paiement}</span></div>}
                    {!p.nif && !p.rccm && !p.id_national && <p className="text-sm text-gray-400 italic">Aucune info légale</p>}
                  </div>
                </div>

                {/* Évolution CA */}
                {evolution.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <h3 className="text-sm font-bold text-gray-800 mb-5 flex items-center gap-2"><TrendingUp size={16} style={{ color: '#448696' }} />Évolution CA (6 mois)</h3>
                    <div className="space-y-3">
                      {evolution.map((e, i) => {
                        const ca = parseFloat(e.ca);
                        const [annee, mois] = e.mois.split('-');
                        const label = `${MOIS_LABELS[parseInt(mois) - 1]} ${annee}`;
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-500 w-20">{label}</span>
                              <span className="text-xs font-bold" style={{ color: '#448696' }}>{formatMontant(ca)}</span>
                            </div>
                            <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${getBarWidth(ca, maxEvo)}%`, backgroundColor: '#448696' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {p.notes && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Notes</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{p.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB: Factures */}
            {detailTab === 'factures' && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                {factures.length === 0 ? (
                  <div className="p-12 text-center text-gray-400"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-sm">Aucune facture liée</p></div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {factures.map(f => {
                      const st = STATUT_FACTURE[f.statut] || STATUT_FACTURE.brouillon;
                      const paye = parseFloat(f.montant_paye || 0);
                      const total = parseFloat(f.montant_total || 0);
                      const pctPaye = total > 0 ? (paye / total * 100) : 0;
                      return (
                        <div key={f.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: f.type_document === 'devis' ? '#fef3c7' : '#f0f9fb' }}>
                              <FileText size={18} style={{ color: f.type_document === 'devis' ? '#d97706' : '#448696' }} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-bold text-gray-900">{f.numero}</span>
                                <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                              </div>
                              <span className="text-xs text-gray-400">{formatDate(f.date_emission)}</span>
                              {/* Mini barre paiement */}
                              {f.type_document === 'facture' && total > 0 && (
                                <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                                  <div className="h-full rounded-full" style={{ width: `${pctPaye}%`, backgroundColor: pctPaye >= 100 ? '#10b981' : '#f59e0b' }} />
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(f.montant_total)}</div>
                              {parseFloat(f.reste_a_payer) > 0 && <div className="text-xs text-red-500 font-semibold">Reste: {formatMontant(f.reste_a_payer)}</div>}
                              {parseFloat(f.reste_a_payer) === 0 && f.type_document === 'facture' && <div className="text-xs text-green-500 font-semibold">Soldé ✓</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB: Paiements */}
            {detailTab === 'paiements' && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                {paiements.length === 0 ? (
                  <div className="p-12 text-center text-gray-400"><CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-sm">Aucun paiement reçu</p></div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {paiements.map((pm, i) => (
                      <div key={i} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50"><CreditCard size={18} className="text-green-600" /></div>
                          <div className="flex-1">
                            <div className="text-sm font-bold text-green-700">{formatMontant(pm.montant)}</div>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              <span>{formatDate(pm.date_paiement)}</span>
                              {pm.facture_numero && <span className="flex items-center gap-1"><FileText size={10} />{pm.facture_numero}</span>}
                              {pm.caisse_nom && <span>{pm.caisse_nom}</span>}
                            </div>
                          </div>
                          <Check size={16} className="text-green-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
      </>
    );
  };

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' ? renderList() : renderDetail()}

      {/* MODAL CRÉATION / ÉDITION */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              {modalMode === 'create' ? <Plus size={24} className="text-white" /> : <Edit2 size={24} className="text-white" />}
              <h3 className="text-xl font-bold text-white">{modalMode === 'create' ? 'Nouveau partenaire' : 'Modifier le partenaire'}</h3>
            </div>

            {/* Type */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
              <div className="flex gap-2">
                {TYPES_PARTENAIRE.map(t => {
                  const Icon = t.icon; const sel = formData.type_partenaire === t.value;
                  return (
                    <button key={t.value} onClick={() => setFormData({ ...formData, type_partenaire: t.value })}
                      className={`flex-1 p-3 rounded-xl text-center transition-all border-2 ${sel ? 'shadow-sm' : 'border-gray-200 hover:bg-gray-50'}`}
                      style={sel ? { borderColor: t.color, backgroundColor: t.bg } : {}}>
                      <Icon size={18} className="mx-auto mb-1" style={{ color: sel ? t.color : '#9ca3af' }} />
                      <div className={`text-xs font-semibold ${sel ? '' : 'text-gray-600'}`} style={sel ? { color: t.color } : {}}>{t.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Champs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Nom / Raison sociale *</label>
                <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} placeholder="Nom complet..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Nom commercial</label>
                <input type="text" value={formData.nom_commercial} onChange={(e) => setFormData({ ...formData, nom_commercial: e.target.value })} placeholder="Optionnel..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                <input type="text" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} placeholder="+242..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone 2</label>
                <input type="text" value={formData.telephone_2} onChange={(e) => setFormData({ ...formData, telephone_2: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Site web</label>
                <input type="text" value={formData.site_web} onChange={(e) => setFormData({ ...formData, site_web: e.target.value })} placeholder="https://..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            </div>
            <div className="mb-4"><label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
              <input type="text" value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Ville</label>
                <input type="text" value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} placeholder="Brazzaville" className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">Pays</label>
                <input type="text" value={formData.pays} onChange={(e) => setFormData({ ...formData, pays: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">NIF</label>
                <input type="text" value={formData.nif} onChange={(e) => setFormData({ ...formData, nif: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">RCCM</label>
                <input type="text" value={formData.rccm} onChange={(e) => setFormData({ ...formData, rccm: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
              <div><label className="block text-sm font-semibold text-gray-700 mb-2">ID National</label>
                <input type="text" value={formData.id_national} onChange={(e) => setFormData({ ...formData, id_national: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>
            </div>
            <div className="mb-6"><label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all" {...inputStyle} /></div>

            <div className="flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleSave} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (modalMode === 'create' ? 'Créer' : 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialogConfig && (
        <ConfirmDialog isOpen={true} title="Archiver ce partenaire" description={`Archiver "${confirmDialogConfig.partenaire.nom}" ?`}
          type="warning" confirmText="Archiver" cancelText="Annuler"
          onClose={() => setConfirmDialogConfig(null)}
          onConfirm={async () => { await handleArchive(confirmDialogConfig.partenaire); setConfirmDialogConfig(null); }} />
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}

      {/* Fermer le menu tri si clic ailleurs */}
      {showTriMenu && <div className="fixed inset-0 z-40" onClick={() => setShowTriMenu(false)} />}
    </div>
  );
};

export default PartenairesModule;